import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { TwilioSmsService, TwilioMessage } from 'src/app/core/services/twilio-sms.service';
import { ApplicationCacheService } from 'src/app/core/services/application-cache.service';
import { ApplicationContextService } from 'src/app/core/services/application-context.service';import { EventService } from 'src/app/core/services/event.service';
import { BroadcastEventTypes } from 'src/app/core/models/lookup.model';
import { Utils } from 'src/app/core/services/utils';
import { MessageTemplateModalComponent } from '../message-template-modal/message-template-modal.component';

@Component({
  selector: 'app-sms-timeline',
  templateUrl: './sms-timeline.component.html',
  styleUrls: ['./sms-timeline.component.scss']
})
export class SmsTimelineComponent implements OnInit, OnDestroy {
  smsMessages: TwilioMessage[] = [];
  loggedInUserName: string = '';
  isLoading: boolean = false;
  hoveredMessageIndex: number | null = null;
  private readonly destroy$ = new Subject<void>();

  private companyUsersMap: Record<string, { firstName: string; lastName: string }> = {};

  constructor(
    private readonly _twilioSmsService: TwilioSmsService,
    private readonly _applicationCacheService: ApplicationCacheService,
    private readonly _appContextService: ApplicationContextService,
    private readonly _modalService: BsModalService,
    private readonly _eventService: EventService,
    private readonly _cdr: ChangeDetectorRef
  ) {
    this._appContextService.context
      .pipe(takeUntil(this.destroy$))
      .subscribe((context) => {
        this.loggedInUserName = `${context.currentlyLoggedInUser.firstName} ${context.currentlyLoggedInUser.lastName}`;
        // Use company users (allAvailableUsers) from application context
        const map = context.companyUsersMap || {};
        this.companyUsersMap = Object.fromEntries(
          Object.entries(map).map(([id, u]) => [id, { firstName: u.firstName ?? '', lastName: u.lastName ?? '' }])
        );
        this.resolveMessageNamesFromCompanyUsers();
      });
  }

  ngOnInit(): void {
    // Wait a bit to ensure patientId is available from cache
    setTimeout(() => {
      this.loadSmsMessages();
    }, 100);
    this._eventService.on(BroadcastEventTypes.SmsMessagesRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadSmsMessages());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get patientId(): string {
    return this._applicationCacheService.applicationCache?.appointment?.patientId || '';
  }

  loadSmsMessages(): void {
    if (!this.patientId) {
      console.warn('SMS Timeline: Patient ID is not available');
      return;
    }

    this.isLoading = true;
    console.log('Loading SMS messages for patient:', this.patientId);
    this._twilioSmsService.getPatientMessages(this.patientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          console.log('SMS API Response:', response);
          
          // Handle different response formats
          let messages: TwilioMessage[] = [];
          
          if (Array.isArray(response)) {
            messages = response;
          } else if (response && Array.isArray(response.data)) {
            messages = response.data;
          } else if (response && response.messages && Array.isArray(response.messages)) {
            messages = response.messages;
          } else if (response && typeof response === 'object') {
            // Try to find any array property
            const arrayKeys = Object.keys(response).filter(key => Array.isArray(response[key]));
            if (arrayKeys.length > 0) {
              messages = response[arrayKeys[0]];
            }
          }
          
          // Sort messages by date (newest first)
          const sortedMessages = messages.sort((b, a) => {
            const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            return dateB - dateA;
          });

          this.smsMessages = sortedMessages;
          this.resolveMessageNamesFromCompanyUsers();
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error loading SMS messages:', err);
this.isLoading = false;
          this.smsMessages = [];
        }
      });
  }

  private resolveMessageNamesFromCompanyUsers(): void {
    const joinMessages = this.smsMessages.filter(m =>
      (m.messageType === 'join_conversation' || m.callType === 'join_conversation') && m.userId
    );
    joinMessages.forEach(m => {
      const user = m.userId ? this.companyUsersMap[m.userId] : null;
      if (user && (user.firstName || user.lastName)) {
        m.firstName = user.firstName;
        m.lastName = user.lastName;
      }
    });
    this._cdr.markForCheck();
  }

  getUserInitials(name: string): string {
    return Utils.getInitialsFromFullName(name);
  }

  getMessageInitials(message: TwilioMessage): string {
    if (message.firstName != null && message.lastName != null && (message.firstName || message.lastName)) {
      return Utils.getInitialsFromFullName(`${message.firstName} ${message.lastName}`);
    }
    const user = message.userId ? this.companyUsersMap[message.userId] : null;
    if (user && (user.firstName || user.lastName)) {
      return Utils.getInitialsFromFullName(`${user.firstName} ${user.lastName}`.trim());
    }
    return message.senderName ? Utils.getInitialsFromFullName(message.senderName) : (message.userId ? '?' : '');
  }

  getMessageDisplayName(message: TwilioMessage): string {
    if (message.firstName != null && message.lastName != null && (message.firstName || message.lastName)) {
      return `${message.firstName} ${message.lastName}`.trim();
    }
    const user = message.userId ? this.companyUsersMap[message.userId] : null;
    if (user && (user.firstName || user.lastName)) {
      return `${user.firstName} ${user.lastName}`.trim();
    }
    return message.senderName || 'Unknown';
  }

  formatMessageDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  isSentMessage(message: TwilioMessage): boolean {
    const isSent = message.messageType === 'sent' || message.direction === 'outbound' || message.callType === 'outbound';
    return isSent && message.senderName !== 'Aya';
  }

  isOutboundCall(message: TwilioMessage): boolean {
    return message.callType === 'outbound' || (message.callType && message.callType.toLowerCase().includes('outbound'));
  }

  onSentMessageHover(message: TwilioMessage, index: number, isHovering: boolean): void {
    this.hoveredMessageIndex = (this.isSentMessage(message) && isHovering) ? index : null;
  }

  openSaveTemplateModal(message: TwilioMessage): void {
    if (!this.isSentMessage(message)) return;
    const initialState = {
      response_message: message.body || '',
      title: ''
    };
    const options: ModalOptions = {
      initialState,
      class: 'modal-md modal-dialog-centered',
      ignoreBackdropClick: false
    };
    this._modalService.show(MessageTemplateModalComponent, options);
  }
}
